import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Button,
    IconButton,
    Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { WarningAmber } from '@mui/icons-material';

import {
    calculateMachinePriceBreakdown,
    calculateFleetPriceBreakdown,
    formatPrice,
    summarizeMachine,
} from './clearBoxConfigUtils';

export default function ClearBoxConfigReviewModal({
    open,
    onClose,
    questionnaire,
    machines = [],
    basePrice,
    additionalLaserPrice,
    onEditMachine,
    onContinue,
}) {
    const steps = questionnaire?.steps ?? [];
    const fleetBreakdown = calculateFleetPriceBreakdown(machines, basePrice, additionalLaserPrice);

    const machineSummaries = machines.map((machine) => ({
        machine,
        ...summarizeMachine(steps, machine.answers),
        priceBreakdown: calculateMachinePriceBreakdown(machine.answers, basePrice, additionalLaserPrice),
    }));

    const hasMissingRequired = machineSummaries.some((m) => m.missingRequiredCount > 0);
    const isContinueDisabled = hasMissingRequired || fleetBreakdown.total === null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            aria-labelledby="config-review-title"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: 'var(--card-shadow)',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f6f8fb 100%)',
                    py: 2.5,
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    pr: 1,
                    pb: 1,
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                    mb: 1.5,
                    px: 3,
                    flexShrink: 0,
                }}
            >
                <DialogTitle
                    id="config-review-title"
                    sx={{
                        flex: 1,
                        fontWeight: 600,
                        pr: 0,
                        fontFamily: 'Avenir Next LT Pro, sans-serif',
                        fontSize: { xs: '1.15rem', sm: '1.35rem' },
                    }}
                >
                    Review your configuration
                </DialogTitle>
                <IconButton onClick={onClose} edge="end" aria-label="close">
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent sx={{ pt: 0, overflow: 'auto', flex: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: { xs: 0.5, sm: 1 } }}>
                    {machineSummaries.map(({ machine, answeredEntries, missingRequiredCount, priceBreakdown }, idx) => (
                        <Box
                            key={machine.id}
                            sx={{
                                border: '1px solid rgba(0,0,0,0.08)',
                                borderRadius: 2,
                                backgroundColor: '#fff',
                                p: { xs: 1.5, sm: 2 },
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#222' }}>
                                        {machine.name}
                                    </Typography>
                                    {missingRequiredCount > 0 && (
                                        <Chip
                                            size="small"
                                            color="warning"
                                            icon={<WarningAmber sx={{ fontSize: 14 }} />}
                                            label={`${missingRequiredCount} required field${missingRequiredCount > 1 ? 's' : ''} missing`}
                                        />
                                    )}
                                </Box>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => onEditMachine(idx)}
                                    sx={{
                                        borderRadius: '999px',
                                        textTransform: 'none',
                                        px: 2,
                                        borderColor: 'rgba(108,152,173,0.7)',
                                        color: 'var(--secondary-color, #6C98AD)',
                                        '&:hover': { borderColor: '#6C98AD', backgroundColor: 'rgba(108,152,173,0.06)' },
                                    }}
                                >
                                    Edit
                                </Button>
                            </Box>

                            {answeredEntries.length > 0 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mb: 1 }}>
                                    {answeredEntries.map((entry) => (
                                        <Typography
                                            key={entry.id}
                                            variant="caption"
                                            sx={{ color: '#666', display: 'flex', justifyContent: 'space-between', gap: 2 }}
                                        >
                                            <span>{entry.title}</span>
                                            <span style={{ textAlign: 'right' }}>{entry.displayValue}</span>
                                        </Typography>
                                    ))}
                                </Box>
                            ) : (
                                <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic', display: 'block', mb: 1 }}>
                                    No questions answered yet.
                                </Typography>
                            )}

                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 700,
                                    color: '#2e7d32',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    borderTop: '1px solid rgba(0,0,0,0.08)',
                                    pt: 0.75,
                                }}
                            >
                                <span>Estimated price</span>
                                <span>{priceBreakdown.total !== null ? formatPrice(priceBreakdown.total) : 'Pending'}</span>
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 1.5,
                    px: 3,
                    pt: 1.5,
                    borderTop: '1px solid rgba(0,0,0,0.06)',
                    flexShrink: 0,
                }}
            >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                    Combined total: {fleetBreakdown.total !== null ? formatPrice(fleetBreakdown.total) : 'Pending'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={onClose}
                        sx={{ borderRadius: '999px', textTransform: 'none', px: 2.5 }}
                    >
                        Back to configuration
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        disabled={isContinueDisabled}
                        onClick={onContinue}
                        sx={{
                            borderRadius: '999px',
                            textTransform: 'none',
                            px: 2.5,
                            backgroundColor: 'var(--secondary-color, #6C98AD)',
                            '&:hover': { backgroundColor: '#5a869c' },
                        }}
                    >
                        Continue
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}
